import TransferRequestFilters, { getDefaultDateRange } from "./TransferRequestFilters";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import { useNavigate, useLocation } from "react-router";
import { useEffect, useRef, useState } from "react";
import { useTONumberAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useTONumberAPI";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setTransferInventoryItems } from "@/app_redux/reducers/slices/auth/inventory_slice";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { FaRegCopy } from "react-icons/fa6";
import { toast } from "react-hot-toast";
import CancelRequestModal from "./CancelRequestModal";
import ViewItemsModal from "./ViewItemsModal";
import PaginationComponent from "@/app/components/ui/Pagination";
import { startCase } from "lodash";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { useUtils } from "@/app/hooks/useUtils";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import {
  IClinicToDoctorListBody,
  IClinicToDoctorRequestItem,
  useClinicToDoctorRequestListAPI,
} from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useClinicToDoctorRequestListAPI";
import { AHS_CLICK_EVENTS, useAhsInventoryClickLog } from "@/app/hooks/useAhsInventoryClickLog";

const TABLE_CELL_CLASS = "text-[0.75rem] font-normal text-black";
const TABLE_CELL_MEDIUM_CLASS = "text-[0.75rem] font-medium text-black";
const TABLE_HEADER_CLASS = "text-[0.875rem] font-semibold text-[#193A6A]";

const PAGE_SIZE = 20;

const DIRECTION_TABS = [
  { label: "Clinic to Doctor", value: "clinic_to_doctor" },
  { label: "Doctor to Clinic", value: "doctor_to_clinic" },
] as const;

const VIEW_ITEMS_ACTIONS = ["view_items", "view items", "view_request", "view request"];

const ClinicToDoctorTransfer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const getTONumber = useTONumberAPI();
  const { NO_APPOINTMENTS } = useImageConstants();
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itrSearchInput, setItrSearchInput] = useState("");
  const [direction, setDirection] = useState<string>(DIRECTION_TABS[0].value);
  const [viewItemsFor, setViewItemsFor] = useState<IClinicToDoctorRequestItem | null>(null);

  const activeFiltersRef = useRef<IClinicToDoctorListBody>({});

  const { execute: fetchList, items, total, loading } = useClinicToDoctorRequestListAPI();
  const { logAhsEvent } = useAhsInventoryClickLog();
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const { statusChipPWL, veticDateFormat } = useUtils();

  dispatch(setTransferInventoryItems({ data: null }));

  const doFetch = (body: IClinicToDoctorListBody, page: number) => {
    fetchList({ ...body, page, page_size: PAGE_SIZE, include_mapped_warehouse: true });
  };

  useEffect(() => {
    if (!mappedClinicId) return;
    const { start_ts, end_ts } = getDefaultDateRange();
    const body: IClinicToDoctorListBody = {
      clinic_ids: [mappedClinicId],
      direction,
      start_ts,
      end_ts,
    };
    activeFiltersRef.current = body;
    setCurrentPage(1);
    doFetch(body, 1);
  }, [mappedClinicId, direction, location.key]);

  const handleFilterSearch = (filters: IClinicToDoctorListBody) => {
    const body: IClinicToDoctorListBody = {
      clinic_ids: filters.clinic_ids?.length ? filters.clinic_ids : mappedClinicId ? [mappedClinicId] : undefined,
      doctor_ids: filters.doctor_ids?.length ? filters.doctor_ids : undefined,
      statuses: filters.statuses?.length ? filters.statuses : undefined,
      start_ts: filters.start_ts,
      end_ts: filters.end_ts,
      direction,
    };
    activeFiltersRef.current = body;
    setItrSearchInput("");
    setCurrentPage(1);
    logAhsEvent(AHS_CLICK_EVENTS.FILTER_APPLY_CLICK, { direction, filter_values: body });
    doFetch(body, 1);
  };

  const handleItrSearch = () => {
    const trimmed = itrSearchInput.trim();
    if (!trimmed) return;
    logAhsEvent(AHS_CLICK_EVENTS.ITR_SEARCH_CLICK, { search_item: trimmed });
    fetchList({ itr_number: trimmed, direction, include_mapped_warehouse: true });
  };

  const handleItrSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleItrSearch();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    doFetch(activeFiltersRef.current, page);
  };

  const handleCreateTransfer = () => {
    logAhsEvent(AHS_CLICK_EVENTS.CREATE_TRANSFER_CLICK, { direction });
    getTONumber();
    navigate("/app/inventory/create-doctor-good-inventory");
  };

  const handleCancelClick = (id: string) => {
    setSelectedRequestId(id);
    setOpenCancelModal(true);
  };

  const handleRequestNumberClick = (item: IClinicToDoctorRequestItem) => {
    logAhsEvent(AHS_CLICK_EVENTS.VIEW_ITEMS_CLICK, {
      event_action: "view_items",
      direction,
      request_id: item.request_id,
      request_number: item.request_number,
      items_count: item.items?.length ?? 0,
    });
    setViewItemsFor(item);
  };

  const handleActionClick = (action: string, item: IClinicToDoctorRequestItem) => {
    const actionEvent =
      action === "cancel"
        ? AHS_CLICK_EVENTS.CANCEL_REQUEST_CLICK
        : VIEW_ITEMS_ACTIONS.includes(action)
          ? AHS_CLICK_EVENTS.VIEW_ITEMS_CLICK
          : action === "edit"
            ? AHS_CLICK_EVENTS.EDIT_REQUEST_CLICK
            : AHS_CLICK_EVENTS.ROW_ACTION_CLICK;
    logAhsEvent(actionEvent, {
      event_action: action,
      direction,
      request_id: item.request_id,
      request_number: item.request_number,
      items_count: item.items?.length ?? 0,
    });
    if (action === "cancel") {
      handleCancelClick(item.request_id);
    }
    if (VIEW_ITEMS_ACTIONS.includes(action)) {
      setViewItemsFor(item);
    }
    if (action === "edit") {
      navigate(`/app/inventory/create-doctor-good-inventory?request_id=${item.request_id}`);
    }
  };

  const columns: TableColumn<IClinicToDoctorRequestItem>[] = [
    {
      id: "created_at",
      header: "Created At",
      width: 160,
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => veticDateFormat(row.created_at) ?? "-",
    },
    {
      id: "status",
      header: "Status",
      className: TABLE_CELL_MEDIUM_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => (
        <div className="flex">
          <span className={statusChipPWL(startCase(row.status))}>{startCase(row.status)}</span>
        </div>
      ),
    },
    {
      id: "request_number",
      header: "Request No.",
      className: TABLE_CELL_MEDIUM_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => (
        <div className="flex gap-2 items-center">
          <span
            className="text-[rgb(49,139,237)] font-medium cursor-pointer hover:underline"
            onClick={() => handleRequestNumberClick(row)}
          >
            {row.request_number}
          </span>
          <div
            className="relative group cursor-pointer"
            data-table-stop-row-click="true"
          >
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Copy
            </div>
            <FaRegCopy
              className="text-gray-400 hover:text-blue-500"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(row.request_number);
                toast.success(`${row.request_number} copied`);
              }}
            />
          </div>
        </div>
      ),
    },
    {
      id: "clinic",
      header: "Clinic",
      className: `${TABLE_CELL_CLASS} max-w-[180px]`,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => row.clinic_name ?? "-",
    },
    {
      id: "doctor",
      header: "Doctor",
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{row.doctor_name ?? "-"}</span>
          {row.doctor_email && <span className="text-gray-400 text-[10px] mt-0.5">{row.doctor_email}</span>}
        </div>
      ),
    },
    {
      id: "items",
      header: "Items",
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) =>
        row.items?.length > 0 ? (
          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-medium">
            {row.items.length} item{row.items.length !== 1 ? "s" : ""}
          </span>
        ) : (
          "-"
        ),
    },
    {
      id: "created_by",
      header: "Created By",
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => row.created_by ?? "-",
    },
    {
      id: "action",
      header: "Action",
      overflow: "visible",
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => renderRowActions(row),
    },
  ];

  const getRowActions = (row: IClinicToDoctorRequestItem) =>
    row.status === "draft"
      ? [...new Set([...(row.action_buttons ?? []), "edit"])]
      : [...new Set(row.action_buttons ?? [])];

  const renderRowActions = (row: IClinicToDoctorRequestItem, alignLeft = false) => {
    const actions = getRowActions(row);
    if (actions.length === 0) return null;
    return (
      <Menu as="div" className="relative inline-block text-left z-10" data-table-stop-row-click="true">
        <Menu.Button
          className="p-1 rounded hover:bg-gray-100 cursor-pointer transition-colors inline-flex justify-center text-gray-700"
          data-table-stop-row-click="true"
        >
          <EllipsisVerticalIcon className="w-5 h-5" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={`absolute top-0 z-50 flex flex-col items-start gap-[15px] p-[16px_20px] min-w-[140px] rounded-[10px] border border-[#E4E7EC] bg-[#FBFBFB] shadow-[0_4px_8px_rgba(0,0,0,0.05)] focus:outline-none ${
              alignLeft ? "left-full ml-2" : "right-full mr-2"
            }`}
          >
            {actions.map((action) => (
              <Menu.Item key={action}>
                <button
                  type="button"
                  onClick={() => handleActionClick(action, row)}
                  className="text-gray-600 text-[0.875rem] capitalize cursor-pointer hover:text-black w-full text-left"
                >
                  {startCase(action)}
                </button>
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    );
  };

  return (
    <>
      {/* Header + Filters */}
      <div className="mx-1 my-2 sm:m-2 rounded-md bg-white px-2 sm:px-4 py-3">
        <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
          {/* Direction tabs */}
          <div className="flex flex-wrap items-center gap-2">
            {DIRECTION_TABS.map((tab) => {
              const isActive = direction === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => {
                    if (direction !== tab.value) {
                      logAhsEvent(AHS_CLICK_EVENTS.DIRECTION_TAB_CLICK, { event_action: tab.value });
                    }
                    setDirection(tab.value);
                    setItrSearchInput("");
                  }}
                  className={`relative inline-flex items-center justify-center overflow-hidden rounded-lg text-sm font-semibold transition-all duration-200 min-w-[120px] sm:min-w-[140px] ${
                    isActive ? "border border-black" : "border border-[#E4E7EC] hover:border-black"
                  }`}
                >
                  <span
                    className={`block w-full rounded-[7px] text-sm px-3 sm:px-4 py-2 text-center text-[11px] leading-4 whitespace-nowrap transition-all duration-200 ${
                      isActive ? "text-black" : "text-[#667085] hover:text-[#344054]"
                    }`}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
          {direction === "clinic_to_doctor" && (
            <button
              onClick={handleCreateTransfer}
              className="bg-[#193A6A] text-white text-xs font-medium px-4 py-2 rounded-md cursor-pointer hover:bg-[#14305a] transition-colors whitespace-nowrap"
            >
              Create Transfer
            </button>
          )}
        </div>
        <TransferRequestFilters key={direction} onSearch={handleFilterSearch} />
      </div>

      {/* ATR Number Search */}
      <div className="mx-1 mt-2 sm:mx-2 rounded-md bg-white px-2 sm:px-4 py-3">
        <InputWithStartAndEndAddOn
          value={itrSearchInput}
          containerClassName="w-full sm:w-1/3"
          placeholder="Search by ATR Number"
          onChange={(e) => setItrSearchInput(e.target.value)}
          onKeyDown={handleItrSearchKeyDown}
          startAddon={<span className="font-semibold text-xs cursor-pointer">ATR #</span>}
          endAddon={
            <button onClick={handleItrSearch} className="font-semibold text-xs cursor-pointer">
              <MagnifyingGlassIcon className="w-4 h-4 hover:text-blue-500 hover:scale-105 transition-transform" />
            </button>
          }
        />
      </div>

      {/* Table */}
      <div className="mx-1 mt-2 sm:mx-2 rounded-md bg-white px-2 sm:px-4 py-3">
        {loading ? (
          <TableSkeleton />
        ) : items?.length > 0 ? (
          <>
            {/* Mobile / tablet card list */}
            <div className="space-y-3 md:hidden">
              {items.map((row, index) => (
                <div
                  key={`${row.request_id}-${index}`}
                  className="rounded-xl border border-[#E4E7EC] bg-white p-3 shadow-sm"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="text-[rgb(49,139,237)] text-sm font-medium cursor-pointer hover:underline truncate"
                        onClick={() => handleRequestNumberClick(row)}
                      >
                        {row.request_number}
                      </span>
                      <FaRegCopy
                        className="text-gray-400 hover:text-blue-500 shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(row.request_number);
                          toast.success(`${row.request_number} copied`);
                        }}
                      />
                    </div>
                    {renderRowActions(row)}
                  </div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className={statusChipPWL(startCase(row.status))}>{startCase(row.status)}</span>
                    <span className="text-[0.75rem] text-gray-400">{veticDateFormat(row.created_at) ?? "-"}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5">
                    <p className={TABLE_CELL_CLASS}>
                      <span className="font-medium">Clinic:</span> {row.clinic_name ?? "-"}
                    </p>
                    <p className={TABLE_CELL_CLASS}>
                      <span className="font-medium">Doctor:</span> {row.doctor_name ?? "-"}
                      {row.doctor_email && (
                        <span className="text-gray-400 text-[10px]"> ({row.doctor_email})</span>
                      )}
                    </p>
                    <p className={TABLE_CELL_CLASS}>
                      <span className="font-medium">Items:</span>{" "}
                      {row.items?.length > 0 ? `${row.items.length} item${row.items.length !== 1 ? "s" : ""}` : "-"}
                    </p>
                    <p className={TABLE_CELL_CLASS}>
                      <span className="font-medium">Created By:</span> {row.created_by ?? "-"}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block">
              <TableComponent
                columns={columns}
                data={items}
                getRowId={(row, index) => `${row.request_id}-${index}`}
                scrollContainerClassName="mt-0 max-h-[calc(100vh-260px)] overflow-y-auto overflow-x-auto"
                minTableWidthClassName="min-w-full"
                tableClassName="rounded-[20px]"
                bodyClassName="text-xs"
              />
            </div>

            {/* Pagination */}
            <div className="mt-3 border-t border-gray-100 pt-3 flex justify-end">
              <PaginationComponent
                className="pagination-bar !float-none"
                currentPage={currentPage}
                totalCount={total}
                pageSize={PAGE_SIZE}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        ) : (
          <div className="w-full flex justify-center py-10">
            <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Data Found." />
          </div>
        )}
      </div>

      <CancelRequestModal
        open={openCancelModal}
        setOpen={setOpenCancelModal}
        selectedRequestId={selectedRequestId}
        clinicId={mappedClinicId}
        onCancelDone={() => {
          const { start_ts, end_ts } = getDefaultDateRange();
          const body = Object.keys(activeFiltersRef.current).length
            ? activeFiltersRef.current
            : { clinic_ids: mappedClinicId ? [mappedClinicId] : undefined, direction, start_ts, end_ts };
          doFetch(body, currentPage);
        }}
      />

      <ViewItemsModal
        open={viewItemsFor !== null}
        onClose={() => setViewItemsFor(null)}
        item={viewItemsFor}
        direction={direction}
        clinicId={mappedClinicId}
        onActionDone={() => {
          const { start_ts, end_ts } = getDefaultDateRange();
          const body = Object.keys(activeFiltersRef.current).length
            ? activeFiltersRef.current
            : { clinic_ids: mappedClinicId ? [mappedClinicId] : undefined, direction, start_ts, end_ts };
          doFetch(body, currentPage);
        }}
      />
    </>
  );
};

export default ClinicToDoctorTransfer;
